/**
 * AISearchMeta Component
 * Renders page metadata as meta tags for AI Search crawler
 *
 * According to AI Search documentation:
 * https://docs.cloud.google.com/generative-ai-app-builder/docs/add-website-metadata
 *
 * AI Search supports meta tags with name and content attributes for advanced website indexing.
 * This component renders page-level metadata (title, description, dates, custom fields) as meta tags
 * that the crawler can pick up and index.
 *
 * This component should be rendered at the page level alongside the standard SEO component.
 * It will only render metadata when inside an AISearchProvider context (indicating a search-enabled page).
 */

import { getImageUrl } from '@/utils/helpers';
import React, { memo, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { AISearchMetaProps } from './AISearchMeta.interface';

/**
 * Prefix for all custom meta tags for search indexing
 * Using a prefix avoids conflicts with standard HTML meta tags
 */
const GEN_META_PREFIX = 'gen:';

/**
 * AISearchMeta component
 * Renders page metadata as meta tags for search crawler indexing
 *
 * Meta tag format: <meta name="gen:fieldName" content="fieldValue" />
 *
 * All meta tags are prefixed with "gen:" to:
 * - Avoid conflicts with standard HTML meta tags
 * - Allow coexistence with standard SEO meta tags
 * - Provide custom structured data for search indexing
 *
 * Search indexing supports:
 * - Custom metadata fields (must match pattern [a-zA-Z0-9][a-zA-Z0-9-_]*)
 * - Multiple values as comma-separated strings
 */
const AISearchMeta: React.FC<AISearchMetaProps> = ({
  metadata,
  contentCategory,
  tags,
  customTags,
  releaseDate,
  publicationDate,
  podcastSeries,
}) => {
  // Build meta tags from page metadata
  // Note: og:image etc. live in SEO; gen:icon here is for the AI Search crawler (icon URL only)
  const metaTags = useMemo(() => {
    const metaTagArray: Array<{ name: string; content: string }> = [];

    if (!metadata) {
      return metaTagArray;
    }

    // gen:icon — prefer search display icon, else social image (same resolution as SEO getImageUrl)
    const icon = getImageUrl(metadata.searchDisplayIcon || metadata.socialMediaImage || {});
    if (icon) {
      metaTagArray.push({ name: `${GEN_META_PREFIX}icon`, content: icon });
    }

    // Process tags to extract taxonomy fields for AI Search meta tags
    // Tags are in format: ["language/es", "language/fr", "content-category/product"]
    // Group by tax key and join multiple values as comma-separated (one meta tag per taxonomy)
    if (tags && Array.isArray(tags) && tags.length > 0) {
      const taxKeyToValues = new Map<string, string[]>();
      tags.forEach((tag) => {
        if (typeof tag === 'string' && tag.includes('/')) {
          const category = tag.split('/')[0];
          if (category) {
            const taxKey = `tax_${category}`;
            if (/^tax_[a-zA-Z0-9][a-zA-Z0-9-_]*$/.test(taxKey)) {
              const existing = taxKeyToValues.get(taxKey) ?? [];
              if (!existing.includes(tag)) {
                existing.push(tag);
                taxKeyToValues.set(taxKey, existing);
              }
            }
          }
        }
      });
      taxKeyToValues.forEach((values, taxKey) => {
        metaTagArray.push({
          name: `${GEN_META_PREFIX}${taxKey}`,
          content: values.join(','),
        });
      });
    }

    if (customTags && Object.keys(customTags).length > 0) {
      Object.entries(customTags).forEach(([key, value]) => {
        metaTagArray.push({
          name: `${GEN_META_PREFIX}${key}`,
          content: value,
        });
      });
    }

    if (contentCategory) {
      metaTagArray.push({
        name: `${GEN_META_PREFIX}tax_content-category`,
        content: contentCategory.join(','),
      });
    }

    if (releaseDate) {
      metaTagArray.push({ name: `${GEN_META_PREFIX}release-date`, content: releaseDate });
    }

    if (publicationDate) {
      metaTagArray.push({ name: `${GEN_META_PREFIX}publication-date`, content: publicationDate });
    }

    if (podcastSeries) {
      metaTagArray.push({ name: `${GEN_META_PREFIX}podcast-series`, content: podcastSeries });
    }

    return metaTagArray;
  }, [metadata, tags, releaseDate, publicationDate, contentCategory, podcastSeries, customTags]);

  // Don't render if no metadata to display
  if (metaTags.length === 0) {
    return null;
  }

  return (
    <Helmet>
      {metaTags.map((tag) => (
        <meta key={tag.name} name={tag.name} content={tag.content} />
      ))}
    </Helmet>
  );
};

export default memo(AISearchMeta);
